import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, doc, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase.ts";
import { useSession } from "../lib/session.tsx";
import { evidenceVisibilityConstraint } from "../lib/evidenceVisibility.ts";

/**
 * The next-best-move assistant.
 *
 * A floating advisor that watches the campaign's live state and proposes the
 * highest-leverage next action. It is rules-based, not a language model: every
 * suggestion is a deterministic read of real data (an open conduct alert,
 * pending evidence, a narrative that broke into news, an approaching deadline),
 * so it can never invent a task that isn't grounded in what's actually there —
 * the same discipline the advisor runs on, applied to workflow.
 *
 * Priority 1 = act now, 2 = soon, 3 = housekeeping. Context nudges: a
 * suggestion whose page you're already on drops down the list.
 */

interface Suggestion {
  id: string;
  priority: 1 | 2 | 3;
  icon: string;
  title: string;
  href: string;
}

const FILING = new Date("2026-08-17T17:00:00-06:00").getTime();

function daysTo(ms: number): number {
  return Math.max(0, Math.ceil((ms - Date.now()) / 86400000));
}

export function Assistant() {
  const { t, locale, profile, can } = useSession();
  const org = profile?.organizationId;
  const loc = useLocation();
  const es = locale === "es";

  const [open, setOpen] = useState(false);
  const [conduct, setConduct] = useState(0);
  const [pendingEvidence, setPendingEvidence] = useState(0);
  const [citable, setCitable] = useState(0);
  const [breakouts, setBreakouts] = useState(0);
  const [flags, setFlags] = useState(0);
  const [overdue, setOverdue] = useState(0);
  const [hasObjectives, setHasObjectives] = useState(true);
  const [goals, setGoals] = useState(0);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // --- Live reads, each gated by permission ------------------------------
  useEffect(() => {
    if (!org) return;
    const subs: Array<() => void> = [];

    subs.push(
      onSnapshot(
        query(
          collection(db, "evidence"),
          where("organizationId", "==", org),
          ...evidenceVisibilityConstraint(can("evidence.restricted.view")),
          limit(500),
        ),
        (s) => {
          const docs = s.docs.map((d) => d.data());
          setPendingEvidence(docs.filter((e) => e.approvalStatus === "pending_review").length);
          setCitable(
            docs.filter((e) => e.approvalStatus === "approved" && e.reliability !== "pending")
              .length,
          );
        },
        () => {},
      ),
    );

    if (can("aura.query")) {
      subs.push(
        onSnapshot(
          query(
            collection(db, "mentions"),
            where("organizationId", "==", org),
            where("principalConduct", "==", true),
            limit(25),
          ),
          (s) => setConduct(s.docs.filter((d) => !d.get("conductAcknowledgedAt")).length),
          () => {},
        ),
      );
      subs.push(
        onSnapshot(
          query(collection(db, "narratives"), where("organizationId", "==", org), limit(25)),
          (s) =>
            setBreakouts(
              s.docs.filter((d) => d.get("breakout") === true && !d.get("breakoutAcknowledgedAt"))
                .length,
            ),
          () => {},
        ),
      );
      subs.push(
        onSnapshot(
          doc(db, "workingMemory", profile!.uid),
          (d) => {
            setHasObjectives(d.exists() && (d.get("objectives")?.length ?? 0) > 0);
          },
          () => {},
        ),
      );
    }
    if (can("compliance.review")) {
      subs.push(
        onSnapshot(
          query(
            collection(db, "complianceFlags"),
            where("organizationId", "==", org),
            where("status", "==", "open"),
            limit(50),
          ),
          (s) => setFlags(s.size),
          () => {},
        ),
      );
    }
    if (can("projects.view")) {
      subs.push(
        onSnapshot(
          query(collection(db, "tasks"), where("organizationId", "==", org), limit(200)),
          (s) => {
            const now = Date.now();
            setOverdue(
              s.docs.filter(
                (d) =>
                  d.get("status") !== "done" && (d.get("dueAt")?.toMillis?.() ?? Infinity) < now,
              ).length,
            );
          },
          () => {},
        ),
      );
    }
    if (can("projects.manage")) {
      subs.push(
        onSnapshot(
          query(collection(db, "goals"), where("organizationId", "==", org), limit(20)),
          (s) => setGoals(s.size),
          () => {},
        ),
      );
    }
    return () => subs.forEach((u) => u());
  }, [org, can, profile]);

  const suggestions = useMemo<Suggestion[]>(() => {
    const out: Suggestion[] = [];
    const filingDays = daysTo(FILING);

    if (conduct > 0)
      out.push({
        id: "conduct",
        priority: 1,
        icon: "⚠",
        title: es
          ? `El candidato está discutiendo con críticos (${conduct}). Sácalo de los comentarios.`
          : `The candidate is arguing with critics (${conduct}). Get him out of the comments.`,
        href: "/watchdog",
      });

    if (breakouts > 0)
      out.push({
        id: "breakout",
        priority: 1,
        icon: "📈",
        title: es
          ? `Una narrativa saltó a las noticias. Informa al equipo.`
          : `A narrative broke into news coverage. Brief the team.`,
        href: "/watchdog",
      });

    if (filingDays > 0 && filingDays <= 14)
      out.push({
        id: "filing",
        priority: 1,
        icon: "⏳",
        title: es
          ? `Faltan ${filingDays} días para el cierre de registro (17 ago).`
          : `${filingDays} days to filing close (Aug 17).`,
        href: "/calendar",
      });

    if (citable === 0)
      out.push({
        id: "noevidence",
        priority: 1,
        icon: "📄",
        title: es
          ? "El asesor no tiene evidencia citable. Aprueba evidencia para activarlo."
          : "The advisor has no citable evidence. Approve evidence to make it useful.",
        href: "/evidence",
      });
    else if (pendingEvidence > 0)
      out.push({
        id: "pending",
        priority: 2,
        icon: "📄",
        title: es
          ? `${pendingEvidence} documento(s) esperan revisión. Apruébalos para que el asesor pueda citarlos.`
          : `${pendingEvidence} document(s) await review. Approve them so the advisor can cite them.`,
        href: "/evidence",
      });

    if (flags > 0)
      out.push({
        id: "flags",
        priority: 2,
        icon: "🚩",
        title: es
          ? `${flags} alerta(s) de cumplimiento abierta(s).`
          : `${flags} open compliance flag(s).`,
        href: "/finance",
      });

    if (can("aura.query") && !hasObjectives)
      out.push({
        id: "objectives",
        priority: 2,
        icon: "🎯",
        title: es
          ? "Define los objetivos de esta sesión para enfocar al asesor."
          : "Set this session's objectives to focus the advisor.",
        href: "/memory",
      });

    if (can("projects.manage") && goals === 0)
      out.push({
        id: "goals",
        priority: 2,
        icon: "🏁",
        title: es
          ? "Define un objetivo de campaña con resultados clave."
          : "Set a campaign goal with key results.",
        href: "/projects",
      });

    if (overdue > 0)
      out.push({
        id: "overdue",
        priority: 3,
        icon: "🕐",
        title: es ? `${overdue} tarea(s) vencida(s).` : `${overdue} overdue task(s).`,
        href: "/work",
      });

    return (
      out
        .filter((s) => !dismissed.has(s.id))
        // De-prioritize a suggestion whose page you're already viewing.
        .sort((a, b) => {
          const ap = a.priority + (loc.pathname === a.href ? 5 : 0);
          const bp = b.priority + (loc.pathname === b.href ? 5 : 0);
          return ap - bp;
        })
    );
  }, [
    conduct,
    breakouts,
    citable,
    pendingEvidence,
    flags,
    hasObjectives,
    goals,
    overdue,
    dismissed,
    loc.pathname,
    can,
    es,
  ]);

  const urgent = suggestions.filter((s) => s.priority === 1).length;

  return (
    <>
      <button
        type="button"
        className="asst-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("asst.title")}
      >
        ✦{urgent > 0 && <span className="asst-badge">{urgent}</span>}
      </button>

      {open && (
        <div className="asst-panel">
          <div className="card-head">
            <div>
              <h3>{t("asst.title")}</h3>
              <span className="faint small">{t("asst.subtitle")}</span>
            </div>
            <button type="button" className="subtle" onClick={() => setOpen(false)}>
              ×
            </button>
          </div>

          {suggestions.length === 0 ? (
            <div className="empty small">{t("asst.none")}</div>
          ) : (
            suggestions.slice(0, 6).map((s) => (
              <div
                key={s.id}
                className="asst-item"
                style={{
                  borderInlineStartColor:
                    s.priority === 1
                      ? "var(--danger)"
                      : s.priority === 2
                        ? "var(--warn)"
                        : "var(--text-faint)",
                }}
              >
                <div className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                  <span style={{ fontSize: "1rem" }}>{s.icon}</span>
                  <span className="small" style={{ flex: 1 }}>
                    {s.title}
                  </span>
                </div>
                <div className="row row-end" style={{ gap: 6, marginTop: 6 }}>
                  <button
                    type="button"
                    className="subtle"
                    style={{ fontSize: "0.7rem" }}
                    onClick={() => setDismissed(new Set([...dismissed, s.id]))}
                  >
                    {t("asst.dismiss")}
                  </button>
                  <Link to={s.href} onClick={() => setOpen(false)}>
                    <button
                      type="button"
                      className="primary"
                      style={{ fontSize: "0.72rem", padding: "3px 10px" }}
                    >
                      {t("asst.go")}
                    </button>
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}
